import { useEffect, useMemo, useState } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { MobileDateRangePicker } from "@material-ui/lab";
import { useTranslation } from "react-i18next";

// Shared date filter — preset shortcuts ("today", "last 7 days", etc.) plus a
// custom range picker. Extracted from the signatures Sent page so the bulk
// messaging log (and any future log/list view) can mount the same UX without
// re-implementing the preset → date-range translation layer.

export type DatePreset =
  | "all"
  | "today"
  | "last7"
  | "last30"
  | "last90"
  | "lastYear"
  | "yearToDate"
  | "thisMonth"
  | "lastMonth"
  | "custom";

/**
 * The DEFAULT menu, not every member of the union: a caller that passes no
 * `presets` gets exactly these. `yearToDate` is deliberately absent — it was
 * added for the listings stats modal, and adding it here would silently change
 * the dropdown on the signatures and bulk-messaging screens too.
 */
export const ALL_DATE_PRESETS: DatePreset[] = [
  "all",
  "today",
  "last7",
  "last30",
  "last90",
  "lastYear",
  "thisMonth",
  "lastMonth",
  "custom"
];

export type DateRange = { from?: Date; to?: Date };

/** Translate a preset choice into a concrete {from,to} range. `undefined`
 *  bounds mean "unfiltered on that side". `custom` is resolved by the caller
 *  from the picker state — this util only handles the named presets. */
export const resolveDatePreset = (preset: DatePreset): DateRange => {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  switch (preset) {
    case "today":
      return { from: startOfToday };
    case "last7":
      return { from: new Date(startOfToday.getTime() - 7 * 24 * 60 * 60 * 1000) };
    case "last30":
      return { from: new Date(startOfToday.getTime() - 30 * 24 * 60 * 60 * 1000) };
    case "last90":
      return { from: new Date(startOfToday.getTime() - 90 * 24 * 60 * 60 * 1000) };
    case "lastYear":
      return { from: new Date(startOfToday.getTime() - 365 * 24 * 60 * 60 * 1000) };
    // Calendar year so far — NOT a rolling window, which is what `lastYear` is.
    case "yearToDate":
      return { from: new Date(now.getFullYear(), 0, 1) };
    case "thisMonth":
      return { from: startOfThisMonth };
    case "lastMonth":
      return {
        from: new Date(now.getFullYear(), now.getMonth() - 1, 1),
        to: startOfThisMonth
      };
    case "custom":
    case "all":
    default:
      return {};
  }
};

/** Build the active range from preset + custom values. Bumps the "to" date
 *  to the start of the next day for inclusive upper-bound semantics, matching
 *  what callers writing `createdTime <= to` expect. */
export const buildActiveDateRange = (
  preset: DatePreset,
  customFrom: Date | null,
  customTo: Date | null
): DateRange => {
  if (preset !== "custom") return resolveDatePreset(preset);
  const from = customFrom
    ? new Date(customFrom.getFullYear(), customFrom.getMonth(), customFrom.getDate())
    : undefined;
  const to = customTo
    ? new Date(customTo.getFullYear(), customTo.getMonth(), customTo.getDate() + 1)
    : undefined;
  return { from, to };
};

/**
 * The picker's masked inputs render as `<input type="tel">` (lab's
 * useMaskedInput does that for the numeric keypad). A global rule in
 * customStyles.css targets that same selector for the phone widget; if it ever
 * sets `width` again, an intrinsically-sized input stops shrinking and the
 * end-date field slides out of the viewport — invisibly in RTL, where the
 * overflow goes off the left edge with nothing scrolling horizontally.
 *
 * The attribute selector is load-bearing: `& input` compiles to (0,1,1), which
 * only TIES a bare `input[type="tel"]` rule — and with `injectFirst` a tie goes
 * to customStyles.css. `& input[type="tel"]` is (0,2,1) and wins outright.
 */
const dateFieldSx = {
  flex: 1,
  minWidth: 0,
  '& input[type="tel"]': { width: "100%" }
};

export type DateRangeFilterValue = {
  preset: DatePreset;
  customFrom: Date | null;
  customTo: Date | null;
};

export const EMPTY_DATE_RANGE_FILTER: DateRangeFilterValue = {
  preset: "all",
  customFrom: null,
  customTo: null
};

type Props = {
  value: DateRangeFilterValue;
  onChange: (next: DateRangeFilterValue) => void;
  /** Translation key prefix for preset labels — defaults to the signatures
   *  namespace because that's where these strings originally lived. Pass
   *  another prefix to override; the keys under it must mirror the names of
   *  the DatePreset union. */
  presetLabelKey?: (preset: DatePreset) => string;
  dateLabel?: string;
  startText?: string;
  endText?: string;
  /** Disabled UI state — used by the bulk messaging log to grey out the
   *  filter while a phone-prefix search is active (the prefix search runs
   *  on the unfiltered query so date constraints would hide results). */
  disabled?: boolean;
  /** Optional helper text shown below the picker when disabled. */
  disabledHelperText?: string;
  /** Min width for the preset Select — useful when laid out next to a
   *  search field that wants to take the remaining space. */
  selectMinWidth?: number | string;
  /** Subset of presets to render in the Select. When omitted, ALL_DATE_PRESETS
   *  is used. Useful for callers (e.g. the listings-board filter drawer)
   *  that only want a handful of options. */
  presets?: DatePreset[];
};

export default function DateRangeFilter({
  value,
  onChange,
  presetLabelKey,
  dateLabel,
  startText,
  endText,
  disabled,
  disabledHelperText,
  selectMinWidth = 160,
  presets
}: Props) {
  const visiblePresets = presets ?? ALL_DATE_PRESETS;
  const { t } = useTranslation();
  const theme = useTheme();
  // Mirror direction-aware arrow glyph used in DocumentsList: in RTL, the
  // start input docks to the right of the end input, so the arrow needs to
  // mirror visually for the user to read it as "from → to".
  const arrowSx = theme.direction === "rtl" ? { transform: "scaleX(-1)" } : undefined;

  // Picker draft: the MobileDateRangePicker fires onChange on every in-modal
  // tap. We keep a local draft so those interim taps don't trigger upstream
  // re-queries — only onAccept commits the draft into the parent value.
  const [pickerDraft, setPickerDraft] = useState<[Date | null, Date | null]>([
    value.customFrom,
    value.customTo
  ]);

  // Re-sync the draft when the parent's applied range changes externally
  // (e.g. "clear filters" elsewhere on the page). Without this, reopening
  // the picker would show stale dates.
  useEffect(() => {
    setPickerDraft([value.customFrom, value.customTo]);
  }, [value.customFrom, value.customTo]);

  const labelFor = useMemo(() => {
    return (preset: DatePreset) =>
      presetLabelKey
        ? t(presetLabelKey(preset))
        : t(`signatures.filters.datePresets.${preset}`);
  }, [presetLabelKey, t]);

  return (
    <Stack spacing={1.5} sx={{ width: "100%" }}>
      <FormControl size="small" sx={{ minWidth: selectMinWidth, opacity: disabled ? 0.5 : 1 }} disabled={disabled}>
        <InputLabel>{dateLabel ?? t("signatures.filters.date")}</InputLabel>
        <Select
          label={dateLabel ?? (t("signatures.filters.date") as string)}
          value={value.preset}
          onChange={(e) =>
            onChange({ ...value, preset: e.target.value as DatePreset })
          }
        >
          {visiblePresets.map((p) => (
            <MenuItem key={p} value={p}>
              {labelFor(p)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {value.preset === "custom" && !disabled && (
        <MobileDateRangePicker
          value={pickerDraft}
          onChange={(range) => setPickerDraft(range as [Date | null, Date | null])}
          onAccept={(range) => {
            const [from, to] = range as [Date | null, Date | null];
            onChange({ ...value, customFrom: from, customTo: to });
          }}
          startText={startText ?? (t("signatures.filters.dateFrom") as string)}
          endText={endText ?? (t("signatures.filters.dateTo") as string)}
          renderInput={(startProps, endProps) => (
            // width:100% because lab's DateRangePickerInputRoot is a flex
            // container — without it the Stack is shrink-to-fit and the fields
            // never claim the space they were given.
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems="center"
              sx={{ width: "100%" }}
            >
              <TextField {...startProps} size="small" sx={dateFieldSx} />
              <Box sx={{ color: "text.secondary", px: 0.5, ...arrowSx }}>→</Box>
              <TextField {...endProps} size="small" sx={dateFieldSx} />
            </Stack>
          )}
        />
      )}

      {disabled && disabledHelperText && (
        <Box sx={{ color: "text.secondary", fontSize: 12, lineHeight: 1.4 }}>
          {disabledHelperText}
        </Box>
      )}
    </Stack>
  );
}
